import React, { useEffect, useState, useRef } from "react";
import { Box, Typography, IconButton, Skeleton } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { logUserAction } from "../../utils/analytics";
import config from "../../config/config";
import axios from "axios";
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ProductAssistantButton from "../AssistantChat/ProductAssistantButton";

const SimilarProductSectionCard = React.memo(({ product, hideBrand = false, trackingContext = {} }) => {
  const navigate = useNavigate();
  const isSearchPage = window.location.pathname === "/search";
  const query = isSearchPage ? localStorage.getItem("query") : null;

  if (!product) return null;

  return (
    <Box
      onClick={() => {
        logUserAction({
          action: "product_click",
          data: {
            product_id: product.product_id || null,
            product_title: product.title || null,
            brand: product.brand || null,
            product_price:
              product?.price?.discounted ||
              product?.price?.original ||
              null,
            currency: product?.price?.currency || null,
            product_image: product?.image_url || null,
            product_url: product?.url || null,
            ...trackingContext
          },
          timestamp: new Date().toISOString(),
        });
        navigate("/product", {
          state: {
            product: product,
            similarProducts: [product],
            searchQuery: query,
            trackingContext,
          },
        });
      }}
      sx={{
        width: "100%",
        height: "100%",
        borderRadius: "12px",
        overflow: "hidden",
        position: "relative",
        cursor: "pointer",
        transition: "transform 0.3s ease, box-shadow 0.3s ease",
        backgroundColor: "#F9F9F9",
        willChange: { xs: "auto", md: "transform" },
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: "0 12px 24px rgba(0,0,0,0.1)",
        },
        "&:active": {
          transform: "translateY(-2px)",
        },
      }}
    >
      <img
        key={product?.image_url || 'no-image'}
        src={product?.image_url}
        alt={product.title}
        loading="lazy"
        onError={(e) => {
          const fallbackImage = product?.images?.[0];
          if (fallbackImage && !e.target.dataset.triedFallback) {
            e.target.dataset.triedFallback = "true";
            e.target.src = fallbackImage;
          } else {
            e.target.onerror = null;
            e.target.src = require("../../assets/images/no_image.png");
          }
        }}
        style={{ width: "100%", height: "100%", objectFit: "cover" }}
      />
      <ProductAssistantButton product={product} />
      {product.price?.discounted && product.price?.original && (
        <Box
          sx={{
            position: "absolute",
            top: 12,
            right: 12,
            background: "#FF3B30",
            color: "#FFF",
            px: 1,
            py: 0.5,
            borderRadius: "4px",
            fontFamily: "Poppins-Bold",
            fontSize: "10px",
            letterSpacing: "0.5px"
          }}
        >
          SALE
        </Box>
      )}

      <Box
        sx={{
          position: "absolute",
          bottom: 0,
          left: 0,
          width: "100%",
          background: "linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0) 100%)",
          px: 1.5,
          pt: 4,
          pb: 1.5,
          display: "flex",
          flexDirection: "column",
          gap: 0.25
        }}
      >
        {!hideBrand && (
          <Typography
            sx={{
              fontFamily: "Poppins-SemiBold",
              fontSize: { xs: "12px", sm: "14px" },
              color: "#FFF",
              letterSpacing: "0.2px",
              textTransform: "uppercase",
              display: "-webkit-box",
              WebkitLineClamp: 1,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              textOverflow: "ellipsis"
            }}
          >
            {product.brand || product.showcaseBrand || "Brand"}
          </Typography>
        )}
        <Typography
          sx={{
            fontFamily: "Poppins-Regular",
            fontSize: { xs: "11px", sm: "13px" },
            color: "rgba(255,255,255,0.9)",
            display: "-webkit-box",
            WebkitLineClamp: 1,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            textOverflow: "ellipsis"
          }}
        >
          {product.title}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, mt: 0.5 }}>
          {product.price?.discounted ? (
            <>
              <Typography
                sx={{
                  fontFamily: "Poppins-SemiBold",
                  fontSize: { xs: "12px", sm: "14px" },
                  color: "#FF5252",
                }}
              >
                {product.price.discounted} {product.price.currency}
              </Typography>
              {product.price.original && (
                <Typography
                  sx={{
                    fontFamily: "Poppins-Regular",
                    fontSize: { xs: "10px", sm: "12px" },
                    color: "rgba(255,255,255,0.6)",
                    textDecoration: "line-through",
                  }}
                >
                  {product.price.original} {product.price.currency}
                </Typography>
              )}
            </>
          ) : (
            <Typography
              sx={{
                fontFamily: "Poppins-SemiBold",
                fontSize: { xs: "12px", sm: "14px" },
                color: "#FFF",
              }}
            >
              {product.price?.original || "N/A"} {product.price?.currency || ""}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
});

const SimilarProductsCard = ({ product }) => {
  const [similarProducts, setSimilarProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const scrollRef = useRef(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);
  const [inView, setInView] = useState(false);
  const containerRef = useRef(null);

  const productUrl = product?.url || product?.product_url;
  const storeName = product?.brand || product?.store_name;

  const trackingContext = React.useMemo(() => ({
    source: "similar_products",
    parent_product_title: product?.title || "",
    parent_product_brand: product?.brand || storeName || "",
  }), [product?.title, product?.brand, storeName]);

  const loggedImpressionRef = useRef(false);

  useEffect(() => {
    if (!productUrl) return;
    
    // Reset logged ref when url changes
    loggedImpressionRef.current = false;

    const fetchSimilar = async () => {
      try {
        setLoading(true);
        const response = await axios.post(`${config.backendUrl}/inventory/product_similar`, {
          product_url: productUrl,
          store_name: storeName,
        });
        
        const fetchedProducts = response.data.products || [];
        setSimilarProducts(fetchedProducts);
      } catch (error) {
        console.error("Error fetching similar products:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchSimilar();
  }, [productUrl, storeName]);

  // Log impressions only when the card scrolls into view
  useEffect(() => {
    if (inView && similarProducts.length > 0 && !loggedImpressionRef.current) {
      loggedImpressionRef.current = true;
      import("../../utils/analytics").then(({ logBatchUserAction }) => {
        const uniqueBrands = [...new Set(similarProducts.map(p => p.brand).filter(Boolean))];
        if (uniqueBrands.length > 0) {
          logBatchUserAction(uniqueBrands.map(brand => ({
            action: "brand_impression",
            data: {
              brand: brand,
              ...trackingContext
            }
          })));
        }
      });
    }
  }, [inView, similarProducts, trackingContext]);

  const checkScroll = () => {
    if (scrollRef.current) {
      const { scrollWidth, clientWidth, scrollLeft } = scrollRef.current;
      setShowLeftArrow(scrollLeft > 0);
      setShowRightArrow(Math.ceil(scrollLeft) < scrollWidth - clientWidth - 5);
    }
  };

  useEffect(() => {
    checkScroll();
    window.addEventListener("resize", checkScroll);
    return () => window.removeEventListener("resize", checkScroll);
  }, [similarProducts]);

  const scrollLeft = () => {
    scrollRef.current?.scrollBy({ left: -300, behavior: "smooth" });
    setTimeout(checkScroll, 350);
  };

  const scrollRight = () => {
    scrollRef.current?.scrollBy({ left: 300, behavior: "smooth" });
    setTimeout(checkScroll, 350);
  };

  useEffect(() => {
    const thresholdValue = window.innerWidth < 900 ? 0.6 : 0.3;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { threshold: thresholdValue }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => {
      if (containerRef.current) observer.unobserve(containerRef.current);
    };
  }, []);

  return (
    <Box ref={containerRef} sx={{ mt: { xs: 4, md: 8 }, mb: 4 }}>
      <style>
        {`
          @keyframes galleryFadeIn {
            0% { opacity: 0; transform: scale(1.05); }
            100% { opacity: 1; transform: scale(1); }
          }
        `}
      </style>
      <Typography
        variant="h4"
        sx={{
          fontFamily: "'Playfair Display', serif",
          fontWeight: 700,
          mb: { xs: 2, md: 4 },
          px: { xs: 3, sm: 0 },
          fontSize: { xs: "24px", md: "32px" },
          position: "relative",
          display: "inline-block",
        }}
      >
        <Box
          component="span"
          sx={{
            background: "linear-gradient(90deg, #1a1a1a 0%, #4a4a4a 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            position: "relative",
            zIndex: 1,
            animation: inView ? `shimmer 2s linear forwards` : "none",
            "@keyframes shimmer": {
              "0%": { backgroundPosition: "150% center" },
              "80%": { backgroundPosition: "-50% center" },
              "100%": { backgroundPosition: "-50% center" },
            },
          }}
        >
          Same Vibe, More Options...
        </Box>
      </Typography>

      <Box sx={{ position: "relative", display: "flex", alignItems: "center" }}>
        <Box
          ref={scrollRef}
          onScroll={checkScroll}
          sx={{
            display: "flex",
            gap: 2,
            overflowX: "auto",
            pb: 2,
            px: { xs: 3, sm: 0 },
            scrollPaddingLeft: { xs: "15px", sm: "0px" },
            scrollSnapType: { xs: "x mandatory", md: "none" },
            "&::-webkit-scrollbar": { display: "none" },
            scrollbarWidth: "none",
            width: "100%",
            transform: "translateZ(0)",
          }}
        >
          {loading ? (
            // Skeleton Loading State
            [1, 2, 3, 4, 5, 6].map(i => (
              <Box
                key={`skeleton-${i}`}
                sx={{
                  minWidth: { xs: "170px", sm: "220px" },
                  maxWidth: { xs: "170px", sm: "220px" },
                  height: { xs: "290px", sm: "320px" },
                  flexShrink: 0,
                  scrollSnapAlign: "start"
                }}
              >
                <Skeleton variant="rectangular" width="100%" height="100%" sx={{ borderRadius: "12px" }} />
              </Box>
            ))
          ) : (
            similarProducts.map((prod, idx) => (
              <Box
                key={idx}
                sx={{
                  minWidth: { xs: "170px", sm: "220px" },
                  maxWidth: { xs: "170px", sm: "220px" },
                  height: { xs: "290px", sm: "320px" },
                  flexShrink: 0,
                  scrollSnapAlign: "start",
                  opacity: 0,
                  animation: inView ? `galleryFadeIn 0.8s cubic-bezier(0.25, 0.8, 0.25, 1) forwards` : "none",
                  animationDelay: `${idx * 0.08}s`,
                }}
              >
                <SimilarProductSectionCard product={prod} hideBrand={false} trackingContext={trackingContext} />
              </Box>
            ))
          )}
        </Box>

        {/* Navigation Arrows for Desktop */}
        {showLeftArrow && (
          <IconButton
            onClick={scrollLeft}
            sx={{
              position: "absolute",
              left: -20,
              top: "50%",
              transform: "translateY(-50%)",
              backgroundColor: "rgba(255,255,255,0.9)",
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              zIndex: 2,
              display: { xs: "none", md: "flex" },
              "&:hover": { backgroundColor: "#fff" },
            }}
          >
            <ArrowBackIosNewIcon fontSize="small" />
          </IconButton>
        )}
        {showRightArrow && (
          <IconButton
            onClick={scrollRight}
            sx={{
              position: "absolute",
              right: -20,
              top: "50%",
              transform: "translateY(-50%)",
              backgroundColor: "rgba(255,255,255,0.9)",
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              zIndex: 2,
              display: { xs: "none", md: "flex" },
              "&:hover": { backgroundColor: "#fff" },
            }}
          >
            <ArrowForwardIosIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
    </Box>
  );
};

export default SimilarProductsCard;
